import React, { FC, useState, useEffect, useCallback } from 'react';
import { Modal, Input, Button, Table, Spin, Tooltip, DatePicker, message } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { BinIcon, CalendarIcon } from 'components/Elements/CustomSVGIcon';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'core/types';
import { MAX_LONG_TEXT_LENGTH } from 'core/constants';
import {
    findStore,
    fetchProjectGuardings,
    createProjectGuarding,
    deleteProjectGuarding,
    FindStoreResult,
    FindStoreMultipleResult,
    isMultipleSites,
} from 'core/ftc/ftcApi';
import { ProjectGuarding } from 'core/sites/types';
import MultipleSitesPicker from '../MultipleSitesPicker';
import './projectGuardingModal.scss';

interface ProjectGuardingModalProps {
    visible: boolean;
    onClose: () => void;
}

// Displayed to the operator; the API always speaks Y-m-d.
const DISPLAY_FORMAT = 'DD/MM/YYYY';
const API_FORMAT = 'YYYY-MM-DD';

const ProjectGuardingModal: FC<ProjectGuardingModalProps> = ({ visible, onClose }) => {
    const dispatch = useDispatch<AppDispatch>();
    const [storeCode, setStoreCode] = useState('');
    const [foundStore, setFoundStore] = useState<FindStoreResult | null>(null);
    // Stores sharing the searched post code, waiting for the user to pick one.
    const [siteChoices, setSiteChoices] = useState<FindStoreMultipleResult | null>(null);
    const [startDate, setStartDate] = useState<Dayjs | null>(null);
    const [endDate, setEndDate] = useState<Dayjs | null>(null);
    const [description, setDescription] = useState('');
    const [entries, setEntries] = useState<ProjectGuarding[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isFinding, setIsFinding] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [removingId, setRemovingId] = useState<number | null>(null);

    const loadEntries = useCallback(async () => {
        setIsLoading(true);
        try {
            const result = await dispatch(fetchProjectGuardings()).unwrap();
            setEntries(result ?? []);
        } catch {
            // Error handled by thunk
        } finally {
            setIsLoading(false);
        }
    }, [dispatch]);

    const resetForm = () => {
        setFoundStore(null);
        setSiteChoices(null);
        setStoreCode('');
        setStartDate(null);
        setEndDate(null);
        setDescription('');
    };

    useEffect(() => {
        if (visible) {
            loadEntries();
        } else {
            resetForm();
            setEntries([]);
        }
    }, [visible, loadEntries]);

    const handleFind = async () => {
        if (!storeCode.trim()) return;

        setIsFinding(true);
        setSiteChoices(null);
        try {
            const result = await dispatch(findStore(storeCode.trim())).unwrap();
            if (isMultipleSites(result)) {
                setFoundStore(null);
                setSiteChoices(result);
                return;
            }
            setFoundStore(result);
            setStoreCode('');
        } catch {
            // Error handled by thunk
        } finally {
            setIsFinding(false);
        }
    };

    const handleSelectSite = (store: FindStoreResult) => {
        setFoundStore(store);
        setSiteChoices(null);
        setStoreCode('');
    };

    // antd won't clamp the end date for us, so moving the start past the end
    // would otherwise post an inverted range the API rejects.
    const handleStartDateChange = (date: Dayjs | null) => {
        setStartDate(date);
        if (date && endDate && endDate.isBefore(date, 'day')) {
            setEndDate(null);
        }
    };

    const handleSave = async () => {
        if (!foundStore || !startDate || !endDate) return;

        setIsSaving(true);
        try {
            await dispatch(createProjectGuarding({
                storeCode: foundStore.storeCode,
                startDate: startDate.format(API_FORMAT),
                endDate: endDate.format(API_FORMAT),
                description: description.trim() || null,
            })).unwrap();
            message.success('Project guarding added');
            resetForm();
            await loadEntries();
        } catch {
            // Error handled by thunk
        } finally {
            setIsSaving(false);
        }
    };

    const handleRemove = async (entryId: number) => {
        setRemovingId(entryId);
        try {
            await dispatch(deleteProjectGuarding(entryId)).unwrap();
            message.success('Project guarding removed');
            await loadEntries();
        } catch {
            // Error handled by thunk
        } finally {
            setRemovingId(null);
        }
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const canSave = !!foundStore && !!startDate && !!endDate;

    const columns = [
        {
            title: 'Store Code',
            dataIndex: 'storeCode',
            key: 'storeCode',
            render: (val: string | null) => val || '-',
        },
        {
            title: 'Store Location',
            dataIndex: 'siteName',
            key: 'siteName',
            render: (val: string | null) => val || '-',
        },
        {
            title: 'From',
            dataIndex: 'startDate',
            key: 'startDate',
            render: (val: string) => (val ? dayjs(val).format(DISPLAY_FORMAT) : '-'),
        },
        {
            title: 'To',
            dataIndex: 'endDate',
            key: 'endDate',
            render: (val: string) => (val ? dayjs(val).format(DISPLAY_FORMAT) : '-'),
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            render: (val: string | null) => val || '-',
        },
        {
            title: ' ',
            key: 'actions',
            width: 60,
            align: 'right' as const,
            className: 'actions-cell',
            render: (_: unknown, record: ProjectGuarding) => (
                <Tooltip title="Remove project guarding">
                    <span>
                        <Button
                            type="link"
                            className="bin-button"
                            loading={removingId === record.id}
                            aria-label="Remove project guarding"
                            onClick={() => handleRemove(record.id)}>
                            {removingId === record.id ? null : <BinIcon w={18} h={19} s="#D90429" />}
                        </Button>
                    </span>
                </Tooltip>
            ),
        },
    ];

    return (
        <Modal
            title="Project Guarding"
            open={visible}
            onCancel={handleClose}
            footer={null}
            centered
            width={900}
            wrapClassName="project-guarding-modal">
            {isLoading ? (
                <div className="modal-loading">
                    <Spin size="large" />
                </div>
            ) : (
                <>
                    <div className="add-store-row">
                        <Input
                            value={storeCode}
                            onChange={(e) => {
                                setStoreCode(e.target.value);
                                // The choices belong to the code that was searched.
                                setSiteChoices(null);
                            }}
                            placeholder="Enter Store No or Post Code"
                            className="store-input"
                            onPressEnter={handleFind} />
                        <Button
                            type="primary"
                            onClick={handleFind}
                            loading={isFinding}
                            className="find-button">
                            Find
                        </Button>
                    </div>

                    {siteChoices && (
                        <MultipleSitesPicker
                            message={siteChoices.message}
                            sites={siteChoices.sites}
                            onSelect={handleSelectSite} />
                    )}

                    {foundStore && (
                        <>
                            <div className="store-info">
                                <span><strong>Store Code:</strong> {foundStore.storeCode || '-'}</span>
                                <span><strong>Store Name:</strong> {foundStore.siteName || '-'}</span>
                                <span><strong>Post Code:</strong> {foundStore.sitePostcode || '-'}</span>
                            </div>

                            <div className="form-row">
                                <div className="field-group">
                                    <label className="field-label">Start Date</label>
                                    <DatePicker
                                        value={startDate}
                                        onChange={handleStartDateChange}
                                        placeholder="Select start date"
                                        format={DISPLAY_FORMAT}
                                        suffixIcon={<CalendarIcon />}
                                        className="date-picker" />
                                </div>
                                <div className="field-group">
                                    <label className="field-label">End Date</label>
                                    <DatePicker
                                        value={endDate}
                                        onChange={setEndDate}
                                        placeholder="Select end date"
                                        format={DISPLAY_FORMAT}
                                        disabledDate={(current) => !!startDate && current.isBefore(startDate, 'day')}
                                        suffixIcon={<CalendarIcon />}
                                        className="date-picker" />
                                </div>
                            </div>

                            <div className="comments-section">
                                <label className="field-label">Description (optional)</label>
                                <Input.TextArea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="e.g. Store refit, project team guarding"
                                    rows={3}
                                    maxLength={MAX_LONG_TEXT_LENGTH}
                                    className="comments-textarea" />
                            </div>
                        </>
                    )}

                    {entries.length > 0 && (
                        <div className="saved-entries">
                            <h4 className="saved-entries-title">
                                Current and upcoming ({entries.length})
                            </h4>
                            <Table
                                dataSource={entries}
                                columns={columns}
                                rowKey="id"
                                pagination={false}
                                size="small"
                                className="entries-table" />
                        </div>
                    )}

                    <div className="modal-buttons">
                        {foundStore && (
                            <Button
                                type="primary"
                                className="save-button"
                                loading={isSaving}
                                disabled={!canSave}
                                onClick={handleSave}>
                                Add Project Guarding
                            </Button>
                        )}
                        <Button
                            className="close-button"
                            onClick={handleClose}>
                            Close
                        </Button>
                    </div>
                </>
            )}
        </Modal>
    );
};

export default ProjectGuardingModal;
